// F3/§2.13: `/embed/g/:grackleId` — the by-id counterpart to EmbedPage. The server always
// serves the generic preview card for `/embed/g/{id}` (Grackle decision 1 on #66: real previews
// only come from share links); this page just plays whatever the viewer can already watch by
// id. No share token, so no ShareGate path — a deny is just NotFound, like any other grackle fetch.
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { WatchOut } from "../api/types";
import NotFound from "../pages/NotFound";
import { PageLoader } from "../components/ui";
import CappedState from "./CappedState";
import { offeredSource } from "./playableSource";
import "./watch.css";

const Player = lazy(() => import("./Player"));

const SpinIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5" />
  </svg>
);
const AlertIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8.5v4.5M12 16.5h.01M10.3 4.3 2.8 17.5A2 2 0 0 0 4.5 20.5h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z" />
  </svg>
);

export default function EmbedGracklePage() {
  const { grackleId } = useParams();
  const [watch, setWatch] = useState<WatchOut | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!grackleId) return;
    try {
      setWatch(await api<WatchOut>("GET", `/watch/${grackleId}`));
    } catch (err) {
      if (err instanceof ApiFail && err.status === 404) setNotFound(true);
    }
  }, [grackleId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (notFound) {
    return <NotFound />;
  }
  // SD60: a processing grackle plays from its master too.
  const source = watch ? offeredSource(watch) : null;

  return (
    <div className="embed-shell">
      {!watch ? (
        <PageLoader />
      ) : watch.capped ? (
        // FR-ST24: the same capped message as the watch page, inside the frame.
        <CappedState title={watch.grackle.title} posterUrl={watch.poster_url} capped={watch.capped} embedded />
      ) : source ? (
        <Suspense fallback={<div className="watch-status-panel" />}>
          <Player
            src={source.src}
            kind={source.kind}
            onSourceExpired={() => void load()}
            poster={watch.poster_url}
            title={watch.grackle.title}
            recordingId={watch.grackle.id}
          />
        </Suspense>
      ) : watch.grackle.status === "failed" ? (
        <div className="watch-status-panel">
          <div className="watch-status-icon tone-failed">{AlertIcon}</div>
          <div className="watch-status-title">Processing failed</div>
        </div>
      ) : (
        <div className="watch-status-panel">
          <div className="watch-status-icon tone-processing">{SpinIcon}</div>
          <div className="watch-status-title">
            {watch.grackle.status === "uploading" ? "Still uploading" : "Still processing"}
          </div>
        </div>
      )}
    </div>
  );
}
